import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CircularProgress, Tooltip } from '@mui/material';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import Loop from '@mui/icons-material/Loop';
import PromptBubble from './PromptBubble';
import SourceFilesMenu from './SourceFilesMenu';
import styles from '../AskAISSE.module.scss';
import { replaceCitationMarkersWithLinks } from '../utils/citations';
import { COMPARE_NAVIGATION_HIGHLIGHT_COLOR } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/constants';

type ChatItem = any;

type Props = {
  fetchPending: boolean;
  showResponseArea: boolean;
  safeHistory: ChatItem[];
  askAiSseCurrentId: any;
  askAiSseHasFirstChunk: boolean;
  askAiSseProgressMessage: string;
  askAiSseAssistantText: any;
  askAiSseError: any;
  askAiSseStatus: string;
  askAiSseCitationsBySource: any;
  fileIdToName: Record<string, string>;
  caseId: string | null;
  onCitationClick: (source: string, citationsBySource: any, compareFiles?: string[]) => void;
  onRestartPrompt?: (prompt: string) => void;
  streamEndRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onScroll: React.UIEventHandler<HTMLDivElement>;
  showJumpToLatest: boolean;
  onJumpToLatest: () => void;
};

const isCitation = (child: any) => {
  if (!React.isValidElement(child)) return false;
  // We may see either:
  // - pre-render ReactMarkdown link elements (with `href`)
  // - post-render citation wrappers (with `data-is-citation`)
  const props: any = child.props || {};
  if (props?.['data-is-citation'] === 'true') return true;
  return typeof props?.href === 'string' && /#citation-(\d+)$/.test(props.href);
};

const isSeparator = (child: any) => {
  if (typeof child === 'string') {
    // Matches whitespace, commas, semicolons, periods, and non-breaking spaces
    return /^[\s,;.\u00A0]*$/.test(child);
  }
  return false;
};

const hashStringToBase36 = (input: string) => {
  // Small deterministic hash for stable React keys (not for security).
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and base36.
  return (hash >>> 0).toString(36);
};

const parsePageNumberFromCoordinates = (coordinates: any) => {
  if (!coordinates) return null;
  try {
    let coordsObj =
      typeof coordinates === 'string'
        ? JSON.parse(coordinates)
        : typeof coordinates === 'object'
          ? coordinates
          : null;
    if (!coordsObj) return null;

    if (Array.isArray(coordsObj)) {
      coordsObj = coordsObj[0];
    }
    if (!coordsObj || typeof coordsObj !== 'object') return null;

    const pageNumberRaw = (coordsObj as any).pageNumber;
    if (pageNumberRaw == null) return null;
    const pageNumber = typeof pageNumberRaw === 'string' ? Number(pageNumberRaw) : pageNumberRaw;
    return Number.isFinite(pageNumber) ? pageNumber : null;
  } catch {
    return null;
  }
};

const hasUsableCoordinates = (coordinates: any) => {
  if (coordinates == null) return false;
  if (Array.isArray(coordinates)) return coordinates.length > 0;
  if (typeof coordinates === 'string') {
    const trimmed = coordinates.trim();
    if (!trimmed) return false;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.length > 0;
      if (parsed && typeof parsed === 'object') return Object.keys(parsed).length > 0;
      return Boolean(parsed);
    } catch {
      // Treat non-JSON strings as present; we only disable for missing/empty arrays.
      return true;
    }
  }
  if (typeof coordinates === 'object') return Object.keys(coordinates).length > 0;
  return true;
};

const buildCaseFileUrl = (
  caseId: string | null | undefined,
  fileId: string | number | null | undefined,
  pageNumber: number | null,
) => {
  if (!caseId || !fileId || pageNumber == null || pageNumber <= 0) return null;
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const caseSegment = encodeURIComponent(String(caseId));
  const fileSegment = encodeURIComponent(String(fileId));
  const pageSegment = encodeURIComponent(String(pageNumber));
  return `${origin}/case/${caseSegment}/files/${fileSegment}?goToPage=${pageSegment}`;
};

const buildCitationUrl = (source: string, citationsBySource: any, caseId: string | null) => {
  if (!citationsBySource) return null;
  const citationObj = citationsBySource?.[String(source)];
  if (!citationObj) return null;
  const metadata = citationObj?.metadata || {};
  const fileId = metadata?.fileId || metadata?.id || citationObj?.fileId;
  if (citationObj?.type === 'document') {
    if (!caseId || !fileId) return null;
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    return `${origin}/case/${encodeURIComponent(String(caseId))}/files/${encodeURIComponent(
      String(fileId),
    )}`;
  }
  const coordinates = citationObj?.coordinates ?? metadata?.coordinates ?? null;
  const pageNumber = parsePageNumberFromCoordinates(coordinates);
  return buildCaseFileUrl(caseId, fileId, pageNumber);
};

const applyCitationLinksToMarkdown = (
  markdownText: string,
  citationsBySource: any,
  caseId: string | null,
) => {
  if (!markdownText) return '';
  return markdownText.replace(/\[(\d+)\]\(#citation-(\d+)\)/g, (_match, label, source) => {
    const url = buildCitationUrl(source || label, citationsBySource, caseId);
    return url ? `[${label}](${url})` : `[${label}]`;
  });
};

const splitTableRow = (line: string) => {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  let escaped = false;

  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (escaped) {
      current += ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === '|') {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const isTableDelimiterRow = (line: string) => {
  if (!line.includes('|')) return false;
  const cells = splitTableRow(line);
  if (cells.length === 0) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()));
};

const markdownToBasicHTML = (markdownText: string): string => {
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const toInlineHTML = (s: string) => {
    let t = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, url) => {
      const trimmed = String(text).trim();
      const displayText = /^\d+$/.test(trimmed) ? `[${trimmed}]` : text;
      return `<a href="${url}">${displayText}</a>`;
    });
    return t;
  };

  const lines = markdownText.split('\n');
  const htmlParts: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      continue;
    }
    const nextLine = lines[i + 1] || '';
    if (line.includes('|') && isTableDelimiterRow(nextLine)) {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      const headers = splitTableRow(line);
      const delimiterCells = splitTableRow(nextLine);
      const alignments = headers.map((_, idx) => {
        const cell = (delimiterCells[idx] || '').trim();
        if (/^:-+:$/.test(cell)) return 'center';
        if (/^-+:$/.test(cell)) return 'right';
        if (/^:-+$/.test(cell)) return 'left';
        return '';
      });
      const rows: string[][] = [];
      i += 2;
      for (; i < lines.length; i++) {
        const rowLine = lines[i];
        if (!rowLine.trim() || !rowLine.includes('|')) break;
        rows.push(splitTableRow(rowLine));
      }
      const renderCell = (cell: string, tag: 'th' | 'td', align: string) => {
        const alignAttr = align ? ` style="text-align:${align}"` : '';
        return `<${tag}${alignAttr}>${toInlineHTML(escapeHtml(cell))}</${tag}>`;
      };
      const thead = `<thead><tr>${headers
        .map((cell, idx) => renderCell(cell, 'th', alignments[idx]))
        .join('')}</tr></thead>`;
      const tbody = rows.length
        ? `<tbody>${rows
            .map((row) => {
              const padded = headers.map((_, idx) => row[idx] || '');
              return `<tr>${padded
                .map((cell, idx) => renderCell(cell, 'td', alignments[idx]))
                .join('')}</tr>`;
            })
            .join('')}</tbody>`
        : '<tbody></tbody>';
      htmlParts.push(`<table>${thead}${tbody}</table>`);
      i -= 1;
      continue;
    }
    if (line.startsWith('### ')) {
      const headerText = line.replace(/^### \*\*(.*?)\*\*$/, '$1').replace(/^### /, '');
      htmlParts.push(`<h4>${toInlineHTML(escapeHtml(headerText))}</h4>`);
      continue;
    }
    if (line.startsWith('## ')) {
      const headerText = line.replace(/^## \*\*(.*?)\*\*$/, '$1').replace(/^## /, '');
      htmlParts.push(`<h3>${toInlineHTML(escapeHtml(headerText))}</h3>`);
      continue;
    }
    if (line.startsWith('# ')) {
      const headerText = line.replace(/^# \*\*(.*?)\*\*$/, '$1').replace(/^# /, '');
      htmlParts.push(`<h2>${toInlineHTML(escapeHtml(headerText))}</h2>`);
      continue;
    }
    if (line.trim() === '---') {
      htmlParts.push('<hr/>');
      continue;
    }
    if (line.startsWith('- ')) {
      if (!inList) {
        inList = true;
        htmlParts.push('<ul>');
      }
      const bullet = line.substring(2);
      htmlParts.push(`<li>${toInlineHTML(escapeHtml(bullet))}</li>`);
      continue;
    }
    if (inList) {
      htmlParts.push('</ul>');
      inList = false;
    }
    htmlParts.push(`<div>${toInlineHTML(escapeHtml(line))}</div>`);
  }
  if (inList) htmlParts.push('</ul>');
  return htmlParts.join('');
};

const copyMarkdownToClipboard = async (markdownText: string) => {
  if (!markdownText) return false;
  try {
    const htmlContent = markdownToBasicHTML(markdownText);
    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      const clipboardItems = [
        new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([markdownText], { type: 'text/plain' }),
        }),
      ];
      await navigator.clipboard.write(clipboardItems);
      return true;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(markdownText);
      return true;
    }
  } catch {
    // Fall through to execCommand fallback.
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = markdownText;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch {
    return false;
  }
};

const getParagraphSignaturePrefix = (nodes: React.ReactNode[], maxLen = 80) => {
  let sig = '';
  for (const n of nodes) {
    if (sig.length >= maxLen) break;
    if (typeof n === 'string') {
      sig += n;
      continue;
    }
    if (React.isValidElement(n)) {
      const props: any = n.props || {};
      if (props?.['data-is-citation'] === 'true') {
        const source = props?.['data-citation-source'];
        sig += `[${typeof source === 'string' ? source : 'citation'}]`;
        continue;
      }
      if (typeof props?.href === 'string') {
        const match = props.href.match(/#citation-(\d+)$/);
        if (match) {
          sig += `[${match[1]}]`;
          continue;
        }
      }
    }
  }
  return sig.slice(0, maxLen);
};

const CitationGroup = ({
  visible,
  hidden,
  expanded,
  onExpand,
  onCollapse,
}: {
  visible: React.ReactNode[];
  hidden: React.ReactNode[];
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
}) => {
  const lastVisible = visible.length > 0 ? visible[visible.length - 1] : null;
  const visibleEndsWithComma = typeof lastVisible === 'string' ? /,\s*$/.test(lastVisible) : false;
  const moreLabel = visibleEndsWithComma ? ' More...' : ', More...';

  return (
    <>
      {visible}
      {expanded ? (
        <>
          {hidden}
          <span
            style={{
              color: '#4567E5',
              cursor: 'pointer',
              marginLeft: '4px',
              fontSize: '0.9em',
              textDecoration: 'none',
            }}
            onClick={onCollapse}
          >
            ...Less
          </span>
        </>
      ) : (
        <span
          style={{
            color: '#4567E5',
            cursor: 'pointer',
            marginLeft: '4px',
            fontSize: '0.9em',
            textDecoration: 'none',
          }}
          onClick={onExpand}
        >
          {moreLabel}
        </span>
      )}
    </>
  );
};

export default function ChatStream(props: Props) {
  const {
    fetchPending,
    showResponseArea,
    safeHistory,
    askAiSseCurrentId,
    askAiSseHasFirstChunk,
    askAiSseProgressMessage,
    askAiSseAssistantText,
    askAiSseError,
    askAiSseStatus,
    askAiSseCitationsBySource,
    fileIdToName,
    caseId,
    onCitationClick,
    onRestartPrompt,
    streamEndRef,
    scrollContainerRef,
    onScroll,
    showJumpToLatest,
    onJumpToLatest,
  } = props;

  const [activeCitation, setActiveCitation] = React.useState<{
    messageId: any;
    sourceId: string;
  } | null>(null);

  const [expandedCitationGroups, setExpandedCitationGroups] = React.useState<
    Record<string, boolean>
  >({});
  const [copiedMessageId, setCopiedMessageId] = React.useState<any>(null);
  const copyResetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const handleCopyAnswer = React.useCallback(
    async (chatId: any, markdownText: string, citationsBySource: any) => {
      const markdownWithLinks = applyCitationLinksToMarkdown(
        markdownText,
        citationsBySource,
        caseId,
      );
      const didCopy = await copyMarkdownToClipboard(markdownWithLinks);
      if (!didCopy) return;
      setCopiedMessageId(chatId);
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    },
    [caseId],
  );

  const renderWithCollapsibleCitationRuns = React.useCallback(
    (children: any, groupBaseKey: string) => {
      const nodes = React.Children.toArray(children);
      const newChildren: React.ReactNode[] = [];
      let currentGroup: React.ReactNode[] = [];
      let citationCount = 0;
      let groupOrdinal = 0;

      const flushGroup = () => {
        if (currentGroup.length === 0) return;
        if (citationCount > 3) {
          let count = 0;
          let splitIndex = 0;
          for (let i = 0; i < currentGroup.length; i++) {
            if (isCitation(currentGroup[i])) {
              count++;
              if (count === 3) {
                splitIndex = i + 1;
                break;
              }
            }
          }
          // Keep any separators right after the 3rd citation in the visible set,
          // so punctuation/spaces don't jump around when collapsed.
          while (splitIndex < currentGroup.length && isSeparator(currentGroup[splitIndex])) {
            const sep = currentGroup[splitIndex];
            // Only keep punctuation separators (commas/semicolons/periods/NBSP), not plain spaces.
            if (typeof sep === 'string' && /[,;.\u00A0]/.test(sep)) {
              splitIndex++;
              continue;
            }
            break;
          }
          const visible = currentGroup.slice(0, splitIndex);
          const hidden = currentGroup.slice(splitIndex);

          const hiddenHasCitation = hidden.some((n) => isCitation(n));
          if (!hiddenHasCitation) {
            newChildren.push(...currentGroup);
          } else {
            const groupId = `${groupBaseKey}-g-${groupOrdinal}`;
            const expanded = Boolean(expandedCitationGroups[groupId]);
            newChildren.push(
              <CitationGroup
                key={groupId}
                visible={visible}
                hidden={hidden}
                expanded={expanded}
                onExpand={() =>
                  setExpandedCitationGroups((prev) => ({
                    ...prev,
                    [groupId]: true,
                  }))
                }
                onCollapse={() =>
                  setExpandedCitationGroups((prev) => ({
                    ...prev,
                    [groupId]: false,
                  }))
                }
              />,
            );
          }
          groupOrdinal++;
        } else {
          newChildren.push(...currentGroup);
        }
        currentGroup = [];
        citationCount = 0;
      };

      for (const child of nodes) {
        if (isCitation(child)) {
          currentGroup.push(child);
          citationCount++;
        } else if (isSeparator(child) && currentGroup.length > 0) {
          currentGroup.push(child);
        } else {
          flushGroup();
          newChildren.push(child);
        }
      }
      flushGroup();

      return newChildren;
    },
    [expandedCitationGroups],
  );

  const showProgress = askAiSseStatus === 'loading' && !askAiSseHasFirstChunk;
  const disableCitationTooltip =
    askAiSseStatus === 'loading' || askAiSseStatus === 'streaming';

  if (fetchPending) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          height: '100%',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!showResponseArea) return null;

  return (
    <div
      ref={scrollContainerRef}
      className={styles.streamContainer}
      data-askai-scroll-container="true"
      onScroll={onScroll}
    >
      <div className={styles.messages}>
        {safeHistory.map((chat: any) => {
          const isActive = chat.id === askAiSseCurrentId;
          const indicatorText = isActive && !askAiSseHasFirstChunk ? askAiSseProgressMessage : '';
          const assistantText = isActive ? askAiSseAssistantText : chat.assistant;
          const assistantErrored = chat.error || askAiSseError;
          const citationsBySource = isActive
            ? askAiSseCitationsBySource || chat.citationsBySource || {}
            : chat.citationsBySource || {};
          const compareFiles = Array.isArray(chat?.compare?.files) ? chat.compare.files : undefined;
          const isComplete = chat.status === 'complete';
          const promptText = String(chat?.prompt ?? '').trim();
          const canRestart = Boolean(onRestartPrompt) && Boolean(promptText);

          const rawMarkdownText = typeof assistantText === 'string' ? assistantText : '';
          const markdownText = replaceCitationMarkersWithLinks(rawMarkdownText);
          const canCopy = isComplete && !assistantErrored && Boolean(rawMarkdownText.trim());

          return (
            <React.Fragment key={chat.id}>
              <PromptBubble text={chat.prompt} />

              <div className={styles.messageGroup}>
                <div className={styles.messageBubble}>
                  {assistantErrored ? (
                    <div className={styles.errorText}>{assistantErrored}</div>
                  ) : (
                    <div className={styles.messageBody}>
                      {isActive && showProgress ? (
                        <div className={styles.progressRow}>
                          <span className={styles.spinner} />
                          <span>{indicatorText || 'Thinking...'}</span>
                        </div>
                      ) : (
                        <Markdown
                          className={styles.messageMarkdown}
                          remarkPlugins={[remarkGfm]}
                          linkTarget="_blank"
                          skipHtml
                          components={{
                            p: ({ children, node }: any) => {
                              const nodes = React.Children.toArray(children);
                              const baseKey =
                                node?.position?.start?.offset != null
                                  ? `m-${String(chat.id)}-p-${String(node.position.start.offset)}`
                                  : `m-${String(chat.id)}-pfx-${hashStringToBase36(
                                      getParagraphSignaturePrefix(nodes),
                                    )}`;
                              return <p>{renderWithCollapsibleCitationRuns(children, baseKey)}</p>;
                            },
                            li: ({ children, node, ...rest }: any) => {
                              const nodes = React.Children.toArray(children);
                              const baseKey =
                                node?.position?.start?.offset != null
                                  ? `m-${String(chat.id)}-li-${String(node.position.start.offset)}`
                                  : `m-${String(chat.id)}-li-pfx-${hashStringToBase36(
                                      getParagraphSignaturePrefix(nodes),
                                    )}`;
                              return (
                                <li {...rest}>
                                  {renderWithCollapsibleCitationRuns(children, baseKey)}
                                </li>
                              );
                            },
                            blockquote: ({ children, node, ...rest }: any) => {
                              const nodes = React.Children.toArray(children);
                              const baseKey =
                                node?.position?.start?.offset != null
                                  ? `m-${String(chat.id)}-bq-${String(node.position.start.offset)}`
                                  : `m-${String(chat.id)}-bq-pfx-${hashStringToBase36(
                                      getParagraphSignaturePrefix(nodes),
                                    )}`;
                              return (
                                <blockquote {...rest}>
                                  {renderWithCollapsibleCitationRuns(children, baseKey)}
                                </blockquote>
                              );
                            },
                            a: ({ href, children }: any) => {
                              const match =
                                typeof href === 'string' ? href.match(/#citation-(\d+)$/) : null;
                              if (!match) {
                                return (
                                  <a href={href} target="_blank" rel="noreferrer">
                                    {children}
                                  </a>
                                );
                              }

                              const source = match[1];
                              const citationObj = citationsBySource?.[String(source)];
                              const metadata = citationObj?.metadata || {};
                              const fileId =
                                metadata?.fileId || metadata?.id || citationObj?.fileId;
                              const fileName =
                                metadata?.fileName ||
                                metadata?.filename ||
                                metadata?.name ||
                                citationObj?.documentName ||
                                (fileId ? fileIdToName[String(fileId)] : null) ||
                                'Unknown';
                              const text = metadata?.text || citationObj?.text || '';
                              const coordinates =
                                citationObj?.coordinates ?? metadata?.coordinates ?? null;
                              const pageNumber = parsePageNumberFromCoordinates(coordinates);
                              const hasPageNumber = pageNumber != null && pageNumber > 0;
                              const isDocumentCitation = citationObj?.type === 'document';
                              const isClickable = isDocumentCitation
                                ? Boolean(fileId)
                                : hasPageNumber && hasUsableCoordinates(coordinates);
                              const compareMatch = (() => {
                                if (!Array.isArray(compareFiles) || compareFiles.length === 0) {
                                  return null;
                                }
                                const fileIdValue = fileId ? String(fileId) : '';
                                if (!fileIdValue) return null;
                                const compareIds = compareFiles
                                  .map((id) => String(id ?? '').trim())
                                  .filter(Boolean);
                                if (compareIds.length === 0) return null;
                                if (compareIds[0] === fileIdValue) return 'pivot';
                                if (compareIds.slice(1).includes(fileIdValue)) return 'compare';
                                return null;
                              })();
                              const compareColor =
                                compareMatch === 'pivot'
                                  ? '#4567E5'
                                  : compareMatch === 'compare'
                                    ? COMPARE_NAVIGATION_HIGHLIGHT_COLOR
                                    : undefined;

                              const isClicked =
                                activeCitation?.messageId === chat.id &&
                                activeCitation?.sourceId === source;

                              const baseStyle: React.CSSProperties =
                                compareColor && isClickable ? { color: compareColor } : {};
                              const activeStyle: React.CSSProperties = isClicked
                                ? {
                                    fontSize: '125%',
                                    fontWeight: 'bold',
                                    border: `2px solid ${compareColor || '#4567E5'}`,
                                    borderRadius: '2px',
                                    textDecoration: 'none',
                                  }
                                : {
                                    textDecoration: 'none',
                                  };
                              const citationStyle: React.CSSProperties = {
                                ...baseStyle,
                                ...activeStyle,
                                ...(disableCitationTooltip && isClickable ? { cursor: 'progress' } : {}),
                              };

                              return (
                                <span data-is-citation="true" data-citation-source={source}>
                                  <Tooltip
                                    arrow
                                    disableHoverListener={disableCitationTooltip}
                                    disableFocusListener={disableCitationTooltip}
                                    disableTouchListener={disableCitationTooltip}
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        className: styles.citationTooltip,
                                      },
                                    }}
                                    title={
                                      disableCitationTooltip ? (
                                        ''
                                      ) : (
                                        <div className={styles.citationPopover}>
                                          <div className={styles.citationPopoverText}>
                                            {text || `Source ${source}`}
                                          </div>
                                          <div className={styles.citationPopoverMeta}>
                                            <div>
                                              <span className={styles.citationMetaLabel}>
                                                Document Name:
                                              </span>{' '}
                                              {fileName}
                                            </div>
                                            <div>
                                              <span className={styles.citationMetaLabel}>
                                                Document Id:
                                              </span>{' '}
                                              {fileId || 'Unknown'}
                                            </div>
                                            {pageNumber != null && (
                                              <div>
                                                <span className={styles.citationMetaLabel}>
                                                  Page Number:
                                                </span>{' '}
                                                {pageNumber}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    }
                                  >
                                    <a
                                      href={href}
                                      className={`${styles.citationLink} ${
                                        !isClickable ? styles.citationLinkDisabled : ''
                                      }`}
                                      style={citationStyle}
                                      aria-disabled={!isClickable}
                                      tabIndex={isClickable ? 0 : -1}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!isClickable) return;
                                        setActiveCitation({
                                          messageId: chat.id,
                                          sourceId: source,
                                        });
                                        onCitationClick(source, citationsBySource, compareFiles);
                                      }}
                                    >
                                      [{source}]
                                    </a>
                                  </Tooltip>
                                </span>
                              );
                            },
                          }}
                        >
                          {markdownText || ''}
                        </Markdown>
                      )}
                    </div>
                  )}
                </div>
                {(canCopy || (isComplete && !assistantErrored)) && (
                  <div className={styles.messageActions}>
                    {canCopy && (
                      <Tooltip
                        title={copiedMessageId === chat.id ? 'Copied' : 'Copy'}
                        placement="top"
                        arrow
                      >
                        <button
                          type="button"
                          className={styles.copyButton}
                          aria-label="Copy answer"
                          onClick={() => handleCopyAnswer(chat.id, markdownText, citationsBySource)}
                        >
                          <CopyAllOutlined fontSize="small" />
                        </button>
                      </Tooltip>
                    )}
                    {isComplete && !assistantErrored && canRestart && (
                      <Tooltip title="Try again" placement="top" arrow>
                        <button
                          type="button"
                          className={styles.copyButton}
                          aria-label="Restart chat"
                          onClick={() => onRestartPrompt?.(promptText)}
                        >
                          <Loop fontSize="small" />
                        </button>
                      </Tooltip>
                    )}
                    {isComplete && !assistantErrored && (
                      <SourceFilesMenu
                        citationsBySource={citationsBySource}
                        fileIdToName={fileIdToName}
                        buttonClassName={styles.copyButton}
                      />
                    )}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
        <div ref={streamEndRef} />
      </div>
      {showJumpToLatest && (
        <div className={styles.jumpToLatestWrap}>
          <Tooltip title="Jump to latest" placement="top" arrow>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.jumpToLatestButton}`}
              aria-label="Jump to latest"
              onClick={onJumpToLatest}
            >
              <ArrowDownward fontSize="small" />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
